import { SizableText, Stack, XStack, YStack } from '@mythical/ui'
import { Button, ButtonLink } from 'app/components/primitive/Button'
import { IconPlay } from 'app/components/primitive/Icon'
import { IconShare } from 'app/components/primitive/Icon/icons/IconShare'
import { Meta } from 'app/components/primitive/Meta'
import { TitleSelect } from 'app/components/primitive/TitleSelect'
import ShareModal from 'app/components/content/ShareModal/ShareModal'
import MainLayout from 'app/components/structure/Layout/MainLayout'
import config from 'app/config'
import { CollectionType } from 'app/queries/collections/collection-fragment'
import { createParam } from 'solito'
import { useState, useMemo, useCallback } from 'react'
import useSeriesLanding from 'app/hooks/use-series-landing'
import Spinner from 'app/components/primitive/Spinner'
import { Grid } from 'app/components/primitive/Grids'
import { ContentCardBookmarkable } from 'app/components/content/ContentCard/ContentCardBookmarkable'
import { ContentCardProps } from 'app/components/content/ContentCard'
import { SolitoImage } from 'solito/image'
import { FullScreenOverlay } from 'app/components/primitive/FullScreenOverlay'
import { Container } from 'app/components/structure/Container'
import {
  WatchLaterProvider,
  useWatchLaterContext,
} from 'app/providers/WatchLaterProvider'

const { useParam } = createParam<{ slug: string; series: string }>()

const sortOptions = [
  { label: 'Recent Episodes', value: '-publishedAt' },
  { label: 'Oldest Episodes', value: 'publishedAt' },
  { label: 'Most Commented', value: '-commentCount' },
  { label: 'Highest Rated', value: '-rating' },
]

interface SeriesContentGridProps {
  items: ContentCardProps[]
  options: Record<string, { numColumns?: number; columnGap?: number; rowGap?: number }>
}

const SeriesContentGrid = ({ items, options }: SeriesContentGridProps) => {
  const { isInWatchLater, toggleWatchLater } = useWatchLaterContext()

  const renderItem = useCallback(
    (item: ContentCardProps) => (
      <ContentCardBookmarkable
        {...item}
        image={item.image2x}
        isInWatchLater={isInWatchLater(item.id)}
        onToggleWatchLater={toggleWatchLater}
        sizes='(min-width: 800px) 450px, (min-width: 660px) 50vw, 100vw'
      />
    ),
    [isInWatchLater, toggleWatchLater],
  )

  return (
    <Grid
      items={items}
      renderItem={renderItem}
      keyExtractor={(item) => item.id}
      options={options}
    />
  )
}

interface SeriesLandingScreenProps {
  type: CollectionType
  slugParam?: 'slug' | 'series'
}

export function SeriesLandingScreen({
  type,
  slugParam = 'slug',
}: SeriesLandingScreenProps) {
  const [slugFromRoute] = useParam(slugParam)
  const slug = slugFromRoute as string
  const [sort, setSort] = useState('-publishedAt')

  const { pagination, collection, lastVideo } = useSeriesLanding(
    type,
    slug || '',
    sort,
  )

  // Use last watched video from the API
  const watchButtonHref = lastVideo?.content?.slug
    ? `/content/${lastVideo.content.slug}`
    : undefined

  const title = collection?.title
  const heroImage = collection?.heroImage
  const shareUrl = `${config.siteUrl}/series/${slug}`
  const items = pagination?.data?.docs || []
  const isInitialLoading = pagination.loading && !collection
  const contentIds = useMemo(() => items.map((item) => item.id), [items])

  return (
    <MainLayout
      showBackBtn
      paddedBottom={false}
      contentStyles={{ fs: 1, fg: 1 }}
    >
      <Meta title={title} type={type} slug={slug} />

      {isInitialLoading && (
        <FullScreenOverlay zIndex={1000} bg="$bgPrimary">
          <Spinner size="large" />
        </FullScreenOverlay>
      )}

      <Container variant="none" fg={1} fs={1} jc="flex-start" paddedX>
        <YStack gap="$6" pb="$3.5" $gtSm={{ gap: '$8.5' }}>
          {!!heroImage && (
            <Stack
              aspectRatio={16 / 9}
              overflow="hidden"
              br="$4"
              $md={{ mx: '$-3.5', mt: '$-3.5', br: 0 }}
            >
              <SolitoImage
                src={heroImage}
                fill
                contentFit="cover"
                sizes="100vw"
                alt=""
              />
            </Stack>
          )}

          <YStack gap="$3.5" $gtSm={{ fd: 'row', ai: 'center' }}>
            <XStack
              gap="$3.5"
              jc="space-between"
              ai="center"
              $gtSm={{ fg: 1, fs: 1 }}
            >
              <SizableText
                tag="h1"
                ff="$heading"
                size="$l"
                color="$fgPrimary"
                fs={1}
                fg={1}
              >
                {title}
              </SizableText>
              <ShareModal
                href={shareUrl}
                trigger={
                  <Button
                    circular
                    theme="secondary"
                    icon={<IconShare size={20} />}
                    aria-label="Share"
                  />
                }
              />
            </XStack>
            {watchButtonHref && (
              <ButtonLink
                href={watchButtonHref}
                theme="primary"
                icon={<IconPlay size={20} />}
              >
                Start Watching
              </ButtonLink>
            )}
          </YStack>

          <TitleSelect
            id="episode-sort"
            value={sort}
            onValueChange={setSort}
            options={sortOptions}
          />
        </YStack>

        <WatchLaterProvider contentIds={contentIds}>
          <SeriesContentGrid
            items={items}
            options={{
              $gtXxxs: { numColumns: 2 },
              $gtSm: { numColumns: 3 },
              $gtLg: { columnGap: 16, rowGap: 24 },
            }}
          />
        </WatchLaterProvider>

        <YStack jc="center" ai="center" pt="$5" gap="$3.5" pb="$3.5">
          {pagination.loading && <Spinner size="large" />}

          {!pagination.loading &&
            pagination.isFetchedAfterMount &&
            !pagination.hasMore &&
            !collection && (
              <YStack jc="center" ai="center" gap="$3.5">
                <SizableText
                  ff="$heading"
                  size="$s"
                  $gtSm={{ size: '$m' }}
                  color="$fgPrimary"
                  ta="center"
                  maw={700}
                  mx="auto"
                >
                  Oops! something's wrong here
                </SizableText>
                <ButtonLink href="/">Reload Homepage</ButtonLink>
              </YStack>
            )}

          {pagination?.hasMore && (
            <Button
              onPress={pagination?.fetchMore}
              loading={pagination?.isFetchingNextPage}
              uppercase
            >
              Load More
            </Button>
          )}
        </YStack>
      </Container>
    </MainLayout>
  )
}
